import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useApiClient } from '@travel/api-client';
import { getL10nNumber } from '@travel/i18n';
import { useTranslation } from '@travel/traveler-core/hooks';
import { CircleScore, StarRating } from '@travel/ui';
import type { LikeIconStyle } from '@travel/ui/components/LikeButton/LikeIcon';
import { cx } from '@travel/utils';

import LikeButton from 'components/LikeButton';

import { updateIsBookmarked } from 'store/bookmarks/actions';
import { addToBookmarks, deleteBookmarkItem } from 'store/bookmarks/api';
import { getDataLayer } from 'store/seo/selectors';

import { Translate } from 'core/translate';
import type { ProviderItem } from 'ProviderList-Types';
import { callTealium } from 'utils/dataLayer';
import { getReviewLabelText, roundReviewScore } from 'utils/review';

import ReviewLink from '../ReviewLink';

import styles from './providerBox.module.scss';

type Props = {
  /** Object of provider data */
  item: ProviderItem;
  /** String of current market */
  market: string;
  className?: string;
};

/**
 * TITLE BOX
 */
export const ProviderTitle = (props: { item: ProviderItem; className?: string }) => {
  return (
    <h2
      className={cx(styles.itemTitle, props.className)}
      data-testid="providerHeaderBox-providerTitleBox-itemTitle"
    >
      {props.item.name}
    </h2>
  );
};

/**
 * TITLE BOX
 */
export const ProviderReview = (props: Props) => {
  const { score, count, label } = props.item.reviews;
  return (
    <ReviewLink
      market={props.market}
      reviewScore={score}
      totalReviews={count}
      reviewLabel={label}
    />
  );
};

export const ProviderReviewPCListView = (props: Props) => {
  const { score, count, label } = props.item.reviews;
  if (!count && !score) {
    return null;
  }
  const scoreLabel = (
    <div className={styles.scoreLabelContainer}>
      <span className={styles.scoreLabel}>{getReviewLabelText(label)}</span>
      <Translate
        className={styles.reviewCount}
        id="Bookmark_List.Reviews"
        condition={() => (count > 1 ? 'count > 1' : 'count == 1')}
        data={{ review_count: <span>{getL10nNumber(props.market, count)}</span> }}
      />
    </div>
  );
  return (
    <CircleScore
      className={styles.circleScore}
      circleIconClassName={styles.circleIcon}
      scoreClassName={styles.scoreText}
      circleClassName={styles.scoreCircle}
      score={roundReviewScore(score)}
      hasAnimation={true}
      strokeWidth={2}
      size={42}
      label={count > 0 && scoreLabel}
    />
  );
};

/**
 * BOOKMARK BUTTON
 */
export const BookmarkButton = (props: {
  providerId: string;
  isBookmarked: boolean;
  className?: string;
  iconSize?: number;
  iconStyle?: LikeIconStyle;
}) => {
  const { providerId, isBookmarked, className, iconSize, iconStyle } = props;

  const dispatch = useDispatch();
  const http = useApiClient();
  const tealiumData = useSelector(getDataLayer);

  const [isAddedAsFavorite, setIsAddedAsFavorite] = useState(isBookmarked);
  const pendingTimerRef = useRef<NodeJS.Timeout | number>(0);
  const isLikedDb = useRef(isBookmarked);

  const isGold = iconStyle === 'gold';

  let likeIconSize = iconSize;
  if (!likeIconSize) {
    likeIconSize = isGold ? 28 : 20;
  }

  useEffect(() => {
    if (isBookmarked !== isLikedDb.current) {
      isLikedDb.current = isBookmarked;
      setIsAddedAsFavorite(isBookmarked);
    }
  }, [isBookmarked]);

  const handleLikeButton = async (isFavorite: boolean) => {
    try {
      if (isLikedDb.current === isFavorite && isFavorite) {
        await deleteBookmarkItem(http, providerId, true);
        isLikedDb.current = false;
      } else if (isLikedDb.current === isFavorite) {
        await addToBookmarks(http, providerId, true);
        isLikedDb.current = true;
        callTealium({
          ...tealiumData,
          bookmark_add_shop: 1,
        });
      }
      dispatch(updateIsBookmarked({ id: providerId, isBookmarked: isLikedDb.current }));
    } catch (_error) {
      setIsAddedAsFavorite((prevIsAddedAsFavorite) => !prevIsAddedAsFavorite);
    }
  };

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  }, []);

  const debounceHandleLikeButton = (e: React.MouseEvent | React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsAddedAsFavorite((prevIsAddedAsFavorite) => !prevIsAddedAsFavorite);
    // pending handler
    clearTimeout(pendingTimerRef.current as number);
    pendingTimerRef.current = setTimeout(() => {
      handleLikeButton(isAddedAsFavorite);
    }, 400);
  };

  return (
    <LikeButton
      className={cx(styles.bookmarkButtonWrapper, className)}
      likeIconClassName={cx(!isGold && styles.likeButton, styles.likeIcon)}
      buttonClassName={isGold ? '' : styles.bookmarkButtonStyle}
      isLiked={isAddedAsFavorite}
      iconStyle={iconStyle}
      likeIconSize={likeIconSize}
      onClick={debounceHandleLikeButton}
      onKeyDown={handleKeyDown}
    />
  );
};

function ProviderHeaderBox(props: Props) {
  const { item, className } = props;
  const starRatingLabel = useTranslation({
    id: 'Traveler_Common.Alt.Star_Rating',
    data: { score: item.starRating },
  });

  return (
    <div className={cx(styles.itemHeader, className)} data-testid="providerHeaderBox-div-wrapper">
      <div className={styles.ratingWrapper} data-testid="providerHeaderBox-starRating">
        {Number(item.starRating) > 0 && (
          <StarRating
            className={styles.starRating}
            rating={item.starRating}
            size={16}
            ariaLabel={starRatingLabel}
          />
        )}
      </div>
    </div>
  );
}

export default ProviderHeaderBox;
